import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import { Button, Link } from '@revelio/ui';

import { kibanaLogger } from '../logging/kibana-logger';
import styles from './RouteErrorFallback.module.css';

const LOG_CONTEXT = 'route-error-element';

// The first line is the Kibana fingerprint and the Slack alert headline, so it
// names only the error; the stack goes on the lines after it.
const toLogMessage = (error: unknown): string => {
  if (!(error instanceof Error)) return `${LOG_CONTEXT}: ${String(error)}`;
  const summary = `${LOG_CONTEXT}: ${error.name}: ${error.message}`;
  return error.stack ? `${summary}\n${error.stack}` : summary;
};

// Replaces the whole route element, so none of the app providers (Chakra,
// UITheme, toasts, urql) are mounted around it.
export const RouteErrorFallback = () => {
  const error = useRouteError();

  useEffect(() => {
    // Reporting is best effort and must never throw: an error raised here
    // re-enters this error element and loops, and a failed log POST must not
    // surface as a second, unhandled error.
    try {
      kibanaLogger
        .error(toLogMessage(error), {
          context: LOG_CONTEXT,
          errorType: 'FE',
          user_facing: true,
        })
        .catch(() => undefined);
    } catch {
      // Nothing else to do on a page that has already crashed.
    }
  }, [error]);

  return (
    <main className={styles.page}>
      <div className={styles.content} role="alert">
        <h1 className={styles.title}>Something went wrong</h1>
        <p className={styles.body}>
          This page hit an error. Reload to try again, or go to the home page.
        </p>
        <div className={styles.actions}>
          <Button color="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
          {/* A full page load rather than client navigation, so in-memory
              state behind the crash does not come along. */}
          <Link href="/" color="accent" underline="rest">
            Go to the home page
          </Link>
        </div>
      </div>
    </main>
  );
};
